/**
 * Missions & questlines (Phase 6). A mission type becomes a CHAIN of tasks
 * anchored to things the generator actually put in this building: a safe that
 * exists, a service plant that exists, a room someone works in. Nothing here
 * invents content — it selects it, which is what keeps a generated objective
 * solvable.
 *
 * Two properties the design turns on:
 *   - Tasks form a DAG via `requires`, and EXFIL depends on all of them, so
 *     "get out" always means "get out having done the job".
 *   - A task may be HIDDEN: absent from the quest log until the thing it's
 *     about enters the player's perception (`discoverBySight`). That's the
 *     observation-discovery hook — you learn the building has a second vault by
 *     seeing the vault, not by being told at briefing.
 *
 * Anchors are stored as descriptors (floor + room + tile), not entity ids:
 * guards and civilians are spawned lazily when a floor is first visited, so the
 * concrete target is BOUND at runtime (`bindTargets`). Pure + headless.
 */

import type { Building } from './dungeon/building';
import type { ServiceKind } from './dungeon/services';
import { mulberry32, hashSeed } from './rng';

export type MissionType =
  | 'assassination' | 'heist' | 'data-theft' | 'recon' | 'extraction' | 'combined';

export type QuestKind =
  | 'eliminate'  // a person, bound at runtime to whoever staffs the anchor room
  | 'steal'      // loot a specific container
  | 'sabotage'   // channel a specific building service
  | 'observe'    // lay eyes on the anchor tile
  | 'rescue'     // reach the extraction target and get them moving
  | 'exfil';     // return to the way in, job done

export interface QuestTask {
  id: number;
  kind: QuestKind;
  /** Log line: what the player is being asked to do. */
  title: string;
  /** Where it happens. */
  floorIdx: number;
  roomId: number;
  x: number;
  y: number;
  /** `steal`: container id. `sabotage`: which service. `eliminate`: bound at
   *  runtime by `bindTargets`. */
  containerId?: number;
  service?: ServiceKind;
  targetEntityId?: number;
  /** Hidden until observed — see `discoverBySight`. */
  hidden: boolean;
  discovered: boolean;
  done: boolean;
  /** Tasks that must be done first. Exfil requires everything. */
  requires: number[];
}

export interface Questline {
  type: MissionType;
  tasks: QuestTask[];
}

/** Tasks the player can currently see in the log. */
export function visibleTasks(q: Questline): QuestTask[] {
  return q.tasks.filter((t) => !t.hidden || t.discovered);
}

/** Is every prerequisite of `t` complete? */
export function unlocked(q: Questline, t: QuestTask): boolean {
  return t.requires.every((id) => q.tasks.find((x) => x.id === id)?.done === true);
}

/** The mission is won when every non-hidden-and-undiscovered task is done —
 *  i.e. everything the player was ever actually asked to do, ending with exfil. */
export function missionComplete(q: Questline): boolean {
  return q.tasks.filter((t) => !t.hidden || t.discovered).every((t) => t.done);
}

/** The floor a mission's payload should live on: the top (`important`) floor,
 *  falling back to the highest that exists. */
function payloadFloor(building: Building): number {
  return building.floors.length - 1;
}

/** Pick a room of one of `kinds` on `floorIdx`, or null. */
function roomOn(
  building: Building,
  floorIdx: number,
  kinds: readonly string[],
): { roomId: number; x: number; y: number } | null {
  const F = building.floors[floorIdx];
  if (!F) return null;
  for (const kind of kinds) {
    const room = F.map.rooms.find((r) => r.kind === kind);
    if (!room) continue;
    return {
      roomId: room.id,
      x: Math.round((room.x0 + room.x1) / 2),
      y: Math.round((room.y0 + room.y1) / 2),
    };
  }
  return null;
}

/** Search every floor, highest first, for a room of one of `kinds`. */
function roomAnywhere(
  building: Building,
  kinds: readonly string[],
): { floorIdx: number; roomId: number; x: number; y: number } | null {
  for (let i = building.floors.length - 1; i >= 0; i--) {
    const hit = roomOn(building, i, kinds);
    if (hit) return { floorIdx: i, ...hit };
  }
  return null;
}

/** The highest-value container in the building — the heist payload. */
function bestSafe(building: Building): { floorIdx: number; roomId: number; x: number; y: number; containerId: number } | null {
  for (let i = building.floors.length - 1; i >= 0; i--) {
    const F = building.floors[i]!;
    // Locked containers first: a safe is a better prize than an open locker.
    const safes = F.map.containers.filter((c) => c.lockId !== null);
    const pick = safes[0] ?? F.map.containers[0];
    if (!pick) continue;
    const room = F.map.rooms.find(
      (r) => pick.x > r.x0 && pick.x < r.x1 && pick.y > r.y0 && pick.y < r.y1,
    );
    return { floorIdx: i, roomId: room?.id ?? 0, x: pick.x, y: pick.y, containerId: pick.id };
  }
  return null;
}

/**
 * Build the questline for a mission type over a specific building. Every task
 * is anchored to something the generator placed; anything that can't be anchored
 * is dropped rather than left dangling, and EXFIL always closes the chain.
 */
export function deriveQuestline(building: Building, type: MissionType, seed: number): Questline {
  const rng = mulberry32(hashSeed(seed, 0x9e57));
  const tasks: QuestTask[] = [];
  let nextId = 1;
  const add = (t: Omit<QuestTask, 'id' | 'discovered' | 'done'>): QuestTask => {
    const task: QuestTask = { ...t, id: nextId++, discovered: false, done: false };
    tasks.push(task);
    return task;
  };

  const top = payloadFloor(building);
  const wanted: MissionType[] = type === 'combined'
    ? (['assassination', 'heist', 'data-theft'] as MissionType[]).filter((_t, i) => i < 2 + Math.floor(rng() * 2))
    : [type];

  for (const t of wanted) {
    if (t === 'assassination') {
      const onTop = roomOn(building, top, ['private-office', 'meeting', 'security']);
      const anchor = onTop
        ? { floorIdx: top, ...onTop }
        : roomAnywhere(building, ['private-office', 'meeting', 'security']);
      if (anchor) {
        const floorIdx = anchor.floorIdx;
        add({
          kind: 'eliminate', title: 'Eliminate the mark', hidden: false, requires: [],
          floorIdx, roomId: anchor.roomId, x: anchor.x, y: anchor.y,
        });
      }
    } else if (t === 'heist') {
      const safe = bestSafe(building);
      if (safe) {
        add({
          kind: 'steal', title: 'Empty the safe', hidden: false, requires: [],
          floorIdx: safe.floorIdx, roomId: safe.roomId, x: safe.x, y: safe.y,
          containerId: safe.containerId,
        });
      }
    } else if (t === 'data-theft') {
      const comms = building.services.find((s) => s.kind === 'comms');
      if (comms) {
        add({
          kind: 'sabotage', title: 'Tap the comms room', hidden: false, requires: [],
          floorIdx: comms.floorIdx, roomId: 0, x: comms.plant.x, y: comms.plant.y,
          service: 'comms',
        });
      }
    } else if (t === 'extraction') {
      // Somebody is being held / hiding somewhere they can be reached.
      const onTop = roomOn(building, top, ['private-office', 'storage', 'meeting']);
      const anchor = onTop
        ? { floorIdx: top, ...onTop }
        : roomAnywhere(building, ['private-office', 'storage', 'meeting']);
      if (anchor) {
        add({
          kind: 'rescue', title: 'Find the client and move them', hidden: false, requires: [],
          floorIdx: anchor.floorIdx, roomId: anchor.roomId, x: anchor.x, y: anchor.y,
        });
      }
    } else if (t === 'recon') {
      const anchor = roomAnywhere(building, ['records', 'server', 'security']);
      if (anchor) {
        add({
          kind: 'observe', title: 'Get eyes on the records', hidden: false, requires: [],
          floorIdx: anchor.floorIdx, roomId: anchor.roomId, x: anchor.x, y: anchor.y,
        });
      }
    }
  }

  // A HIDDEN follow-up: the building's second prize, which the player only
  // learns about by seeing it. Anchored to a vault-ish room away from the main
  // job so discovery means actually exploring.
  const hiddenAnchor = roomAnywhere(building, ['records', 'server', 'storage']);
  if (hiddenAnchor && tasks.length > 0) {
    add({
      kind: 'observe', title: 'Photograph what you found', hidden: true, requires: [],
      floorIdx: hiddenAnchor.floorIdx, roomId: hiddenAnchor.roomId,
      x: hiddenAnchor.x, y: hiddenAnchor.y,
    });
  }

  // Exfil closes the chain and depends on every visible task before it.
  const ground = building.floors[building.activeFloorIdx]!;
  add({
    kind: 'exfil', title: 'Get out the way you came in', hidden: false,
    requires: tasks.filter((t) => !t.hidden).map((t) => t.id),
    floorIdx: building.activeFloorIdx, roomId: 0,
    x: ground.map.spawn.x, y: ground.map.spawn.y,
  });

  return { type, tasks };
}

/**
 * Reveal a hidden task when its anchor enters the player's view. `visible` is
 * asked whether a tile on the ACTIVE floor is currently seen; returns the tasks
 * that were revealed this call so the caller can announce them.
 */
export function discoverBySight(
  q: Questline,
  floorIdx: number,
  visible: (x: number, y: number) => boolean,
): QuestTask[] {
  const found: QuestTask[] = [];
  for (const t of q.tasks) {
    if (!t.hidden || t.discovered || t.floorIdx !== floorIdx) continue;
    if (!visible(t.x, t.y)) continue;
    t.discovered = true;
    found.push(t);
  }
  return found;
}

/**
 * Bind the runtime entity for tasks that target a PERSON, now that the floor's
 * NPCs exist. The mark is whoever occupies the anchor room — a civilian if the
 * room is staffed (an executive at their desk), else the guard standing there.
 */
export function bindTargets(
  q: Questline,
  floorIdx: number,
  occupants: ReadonlyArray<{ id: number; x: number; y: number }>,
  roomOf: (x: number, y: number) => number | null,
): void {
  for (const t of q.tasks) {
    if (t.kind !== 'eliminate' || t.floorIdx !== floorIdx || t.targetEntityId !== undefined) continue;
    const inRoom = occupants.find((o) => roomOf(o.x, o.y) === t.roomId);
    const target = inRoom ?? occupants[0];
    if (target) t.targetEntityId = target.id;
  }
}

/**
 * Per-tick completion pass. Everything a task can be completed by is something
 * an existing system already tracks — a dead entity, a looted container, a
 * tampered service, a seen tile — so questing adds no new state to the world,
 * only a reading of it.
 *
 * Returns the tasks completed this call, for the caller to announce.
 */
export function updateQuests(
  q: Questline,
  ctx: {
    floorIdx: number;
    /** Is this entity dead? */
    isDead: (entityId: number) => boolean;
    /** Has this container been emptied? */
    isLooted: (containerId: number) => boolean;
    /** Has this service been tampered with? */
    isTampered: (kind: ServiceKind) => boolean;
    /** Is this tile on the active floor currently visible to the player? */
    isVisible: (x: number, y: number) => boolean;
    /** The player's tile, for exfil. */
    playerX: number;
    playerY: number;
    /** Is the extraction target moving with the player? Undefined on contracts
     *  that don't involve one. */
    escortFollowing?: boolean;
    /** Is the extraction target close enough at the exit to count as extracted? */
    escortSecured?: boolean;
  },
): QuestTask[] {
  const done: QuestTask[] = [];
  for (const t of q.tasks) {
    if (t.done || (t.hidden && !t.discovered)) continue;
    if (!unlocked(q, t)) continue;
    let complete = false;
    switch (t.kind) {
      case 'eliminate':
        complete = t.targetEntityId !== undefined && ctx.isDead(t.targetEntityId);
        break;
      case 'steal':
        complete = t.containerId !== undefined && ctx.isLooted(t.containerId);
        break;
      case 'sabotage':
        complete = t.service !== undefined && ctx.isTampered(t.service);
        break;
      case 'observe':
        complete = t.floorIdx === ctx.floorIdx && ctx.isVisible(t.x, t.y);
        break;
      case 'rescue':
        complete = ctx.escortFollowing === true;
        break;
      case 'exfil':
        // Walking out alone doesn't extract anyone: if this contract has a
        // client, they have to be at the door with you.
        complete = t.floorIdx === ctx.floorIdx
          && Math.max(Math.abs(ctx.playerX - t.x), Math.abs(ctx.playerY - t.y)) <= 1
          && ctx.escortSecured !== false;
        break;
    }
    if (complete) {
      t.done = true;
      done.push(t);
    }
  }
  return done;
}
